import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useCallback } from 'react';
import { TFunction } from 'i18next';
import { useModal } from './useDialog';
import useLocalization from './useLocalization';
import { useLogin } from '../mutations/useLogin';
import { getPrefillConfigFromGB } from '../utils/growthbook';
import { LOGIN_MODE, type LoginMode } from '../types/enums';

const getEmailSchema = (t: TFunction) =>
  z.object({
    email: z
      .string()
      .nonempty({ error: t('register.validation.requiredField') })
      .pipe(z.email(t('register.validation.invalidEmail'))),
    username: z.string().optional(),
    password: z.string().min(8, t('register.validation.passwordTooShort')),
  });

const getUsernameSchema = (t: TFunction) =>
  z.object({
    email: z.string().optional(),
    username: z.string().nonempty({ error: t('register.validation.requiredField') }),
    password: z.string().nonempty({ error: t('register.validation.requiredField') }),
  });

export type EmailLoginFormData = z.infer<ReturnType<typeof getEmailSchema>>;
export type UsernameLoginFormData = z.infer<ReturnType<typeof getUsernameSchema>>;
export type LoginFormData = EmailLoginFormData | UsernameLoginFormData;
type UseLoginFormOptions = {
  mode?: LoginMode;
};

export const useLoginForm = (options?: UseLoginFormOptions) => {
  const mode = options?.mode ?? LOGIN_MODE.EMAIL;
  const isUsernameMode = mode === LOGIN_MODE.USERNAME;

  const { t } = useLocalization();
  const { closeModal } = useModal();
  const { mutateAsync: login } = useLogin(closeModal);

  const loginSchema = isUsernameMode ? getUsernameSchema(t) : getEmailSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<z.input<typeof loginSchema>, unknown, z.output<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: getPrefillConfigFromGB(),
  });

  const handleClose = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const onSubmit = async (data: z.output<typeof loginSchema>) => {
    const identifier = (isUsernameMode ? data.username : data.email) ?? '';

    try {
      await login({ email: identifier, password: data.password });
    } catch {
      // The login mutation displays the translated authentication error.
    }
  };

  return {
    t,
    control,
    errors,
    isSubmitting,
    handleClose,
    onSubmit: handleSubmit(onSubmit),
    mode,
  };
};
